import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'core/store';
import {
  IonBreadcrumb,
  IonBreadcrumbs,
  IonButton,
  IonContent,
  IonIcon,
  IonModal,
  IonPage,
  IonRow,
  IonText,
  IonToolbar
} from '@ionic/react';
import { useHistory } from 'react-router';
import Manual from 'assets/icons/manuals.svg';
import Image from 'assets/icons/image.svg';
import Contracts from 'assets/icons/contracts.svg';
import Warranty from 'assets/icons/warranty.svg';
import HoaCoa from 'assets/icons/hoa_coa.svg';
import Misc from 'assets/icons/misc_docs.svg';
import Folder from 'assets/icons/folder.svg';
import { HKPlatform } from 'core/constants';
import './DocumentsGroups.scss';
import ArrowRight from 'assets/icons/arrow-right.svg';
import ListItem from 'components/items/list-item/ListItem';
import SearchBar from 'components/searchbar/SearchBar';
import LoadingIonList from 'core/layout/loading-ion-list/LoadingIonList';
import MenuHeader from 'core/layout/menu-header/MenuHeader';
import {
  hideDocumentsAddModal,
  hideDesktopDocumentEditModal,
  resetDocuments,
  resetSearchedDocuments,
  setSelectedDocument,
  showDesktopDocumentEditModal,
  showDocumentsAddModal
} from 'features/shared/documents/DocumentsSlice';
import {
  fetchDocumentCount,
  fetchDocumentTypes,
  fetchSearchedDocuments
} from 'features/shared/documents/DocumentsActions';
import DocumentsAdd from 'features/shared/documents/DocumentsAdd';
import { SearchedDocument } from 'core/types';
import DocumentCard from 'components/cards/document-card/DocumentCard';
import { getDocumentImageSource, isFileTypeVideo } from 'core/util';
import { useVideoThumbnail } from 'core/hooks/useVideoThumbnail';
import DocumentEdit from 'features/shared/documents/DocumentEdit';

export enum HKDocumentType {
  MANUALS = 'manuals',
  VIDEOS = 'videos',
  FLOOR_PLANS = 'floorplans',
  PROPERTY_RECORDS = 'property_records',
  LOAN_DOCUMENTS = 'loan_documents',
  WARRANTY = 'warranty',
  HOA_COA = 'hoa_coa',
  MISC = 'misc',
  CONSTRUCTION = 'construction'
}

const DocumentsGroups: React.FC = () => {
  const {
    loading,
    counts,
    searchedDocuments,
    addModalVisible,
    editDesktopModalVisible
  } = useSelector((state: RootState) => state.documents);
  const homeId = useSelector((state: RootState) => state.home.currentHome?.id);
  const { platformType, isDesktopWidth } = useSelector(
    (state: RootState) => state.platform
  );
  const [search, setSearch] = useState('');
  const history = useHistory();
  const dispatch = useDispatch();

  const generateListOfSearchedGroups = () => {
    let listOfGroups: any[] = [];
    let uniqueListOfGroups = new Set();
    searchedDocuments.forEach((document) => {
      const group = document.type_label;
      uniqueListOfGroups.add(group);
    });
    listOfGroups = Array.from(uniqueListOfGroups).filter((group) => {
      return group !== undefined;
    });
    return listOfGroups;
  };

  const filteredDocumentsGroups = generateListOfSearchedGroups();

  const getEnhancedCounts = () => {
    const enhancedCounts = [...(counts || [])];

    // Separate all construction folders from regular folders
    const constructionSubfolders = enhancedCounts.filter((item) =>
      item.type.startsWith('construction')
    );
    let regularFolders = enhancedCounts.filter(
      (item) => !item.type.startsWith('construction')
    );

    // Calculate total count for construction folder (all construction folders)
    const constructionTotalCount = constructionSubfolders.reduce(
      (total, folder) => total + folder.count,
      0
    );

    // Always add the static Construction folder
    regularFolders.push({
      label: 'Construction',
      count: constructionTotalCount,
      type: HKDocumentType.CONSTRUCTION,
      group: 'Construction'
    });

    // Sort with Construction and Manuals first, then alphabetically
    return regularFolders.sort((a, b) => {
      // Define priority order
      const priorityOrder = ['Construction', 'Manuals'];

      const aIndex = priorityOrder.indexOf(a.label);
      const bIndex = priorityOrder.indexOf(b.label);

      // If both are priority items, sort by their priority order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      // If only a is priority, it comes first
      if (aIndex !== -1) {
        return -1;
      }

      // If only b is priority, it comes first
      if (bIndex !== -1) {
        return 1;
      }

      // If neither is priority, sort alphabetically
      return a.label.localeCompare(b.label);
    });
  };

  const imageForDocumentType = (type: string): string => {
    switch (type) {
      case HKDocumentType.MANUALS:
        return Manual;
      case HKDocumentType.VIDEOS:
        return Image;
      case HKDocumentType.FLOOR_PLANS:
        return Contracts;
      case HKDocumentType.PROPERTY_RECORDS:
        return Contracts;
      case HKDocumentType.LOAN_DOCUMENTS:
        return Manual;
      case HKDocumentType.WARRANTY:
        return Warranty;
      case HKDocumentType.HOA_COA:
        return HoaCoa;
      case HKDocumentType.MISC:
        return Misc;
      case HKDocumentType.CONSTRUCTION:
        return Folder;
    }
    return Folder;
  };

  const openDesktopDocument = (document: SearchedDocument) => {
    dispatch(setSelectedDocument(document));
    dispatch(showDesktopDocumentEditModal());
  };

  const showGroup = (group: string) => {
    if (group !== undefined) {
      dispatch(resetDocuments());
      if (group === HKDocumentType.CONSTRUCTION) {
        // Navigate to construction subfolders view
        history.push(`/documents/group/construction`);
      } else {
        history.push(`/documents/group/${group}`);
      }
    }
  };

  const closeAddDocumentsModal = () => {
    if (!!homeId) {
      dispatch(fetchDocumentCount(homeId));
    }
    dispatch(hideDocumentsAddModal());
  };

  useEffect(() => {
    if (!!homeId) {
      dispatch(fetchDocumentCount(homeId));
      dispatch(fetchDocumentTypes());
    }
  }, [homeId]);

  useEffect(() => {
    if (!!homeId && !!search) {
      dispatch(fetchSearchedDocuments(homeId, search));
    } else {
      dispatch(resetSearchedDocuments());
    }
  }, [search, editDesktopModalVisible]);

  function SearchedDocumentsView() {
    return (
      <>
        {searchedDocuments.length ? (
          <>
            {filteredDocumentsGroups.map((group: string, i: number) => {
              return (
                <div
                  className={
                    platformType === HKPlatform.DESKTOP && isDesktopWidth
                      ? 'hk-desktop-searched-products'
                      : 'hk-searched-products'
                  }
                  key={`group-${i}`}
                >
                  <h4>{group}</h4>
                  {searchedDocuments!.map(
                    (searchedDocument: SearchedDocument, i: number) => {
                      const { type_label } = searchedDocument;
                      return (
                        <>
                          {type_label === group && (
                            <SearchedDocumentItem
                              key={`item-${i}`}
                              document={searchedDocument}
                              onDocumentClick={openDesktopDocument}
                            />
                          )}
                        </>
                      );
                    }
                  )}
                </div>
              );
            })}
          </>
        ) : (
          <div className="hk-search-empty">
            <h1>No Matches Found</h1>
            <p>
              The thing you are searching for isn't here. Please try another
              search.
            </p>
            <p>
              You can also add a document
              {platformType === HKPlatform.DESKTOP && isDesktopWidth
                ? ' above'
                : ' below'}
              .
            </p>
          </div>
        )}
      </>
    );
  }

  function DesktopDocumentsGroupView() {
    return (
      <>
        <IonRow className="hk-desktop-digital-documents-group-header">
          <div className="hk-desktop-digital-documents-group-header-container">
            <IonBreadcrumbs className="hk-desktop-digital-documents-group-header-breadcrumbs">
              <IonBreadcrumb>Documents</IonBreadcrumb>
            </IonBreadcrumbs>
            <div className="hk-desktop-digital-documents-group-header-right ">
              <div className="hk-desktop-digital-documents-group-search">
                <SearchBar
                  value={search}
                  disabled={getEnhancedCounts()?.length === 0}
                  debounce={1000}
                  onSearchChange={(e) => setSearch(e)}
                ></SearchBar>
              </div>
              <div className="hk-desktop-digital-documents-group-header-button">
                <IonButton
                  expand="block"
                  color="primary"
                  strong={true}
                  onClick={() => dispatch(showDocumentsAddModal())}
                >
                  Upload Document
                </IonButton>
              </div>
            </div>
          </div>
        </IonRow>
        <IonContent className="hk-desktop-digital-documents-group">
          <LoadingIonList
            className="hk-desktop-digital-documents-group-list"
            label="Documents"
            items={getEnhancedCounts()}
            loading={loading}
          >
            {!!search ? (
              <SearchedDocumentsView />
            ) : (
              <>
                {getEnhancedCounts()?.map((documentCount: any, i: any) => {
                  const { label, count, type } = documentCount;
                  return (
                    <ListItem
                      key={`item-${i}`}
                      customView={true}
                      onClick={() => showGroup(type)}
                    >
                      <IonIcon
                        slot="start"
                        icon={imageForDocumentType(type)}
                        className="hk-desktop-digital-documents-group-list-item-start-icon"
                      />
                      <div className="hk-desktop-digital-documents-group-list-item-body">
                        <IonText className="hk-desktop-digital-documents-group-list-item-body-title">
                          <h2>{label}</h2>
                        </IonText>
                        <IonText className="hk-desktop-digital-documents-group-list-item-body-description">
                          <p>{count + ' items'}</p>
                        </IonText>
                        <IonIcon
                          slot="end"
                          icon={ArrowRight}
                          className="hk-desktop-digital-documents-group-list-item-body-end-icon"
                        ></IonIcon>
                      </div>
                    </ListItem>
                  );
                })}
              </>
            )}
          </LoadingIonList>
        </IonContent>
      </>
    );
  }

  function MobileDocumentsGroupView() {
    return (
      <>
        <MenuHeader title={'Documents'} />
        <IonContent fullscreen>
          <SearchBar
            value={search}
            disabled={getEnhancedCounts()?.length === 0}
            debounce={1000}
            onSearchChange={(e) => setSearch(e)}
          ></SearchBar>
          <LoadingIonList
            label="Documents"
            items={getEnhancedCounts()}
            loading={loading}
          >
            {!!search ? (
              <SearchedDocumentsView />
            ) : (
              <>
                {getEnhancedCounts()?.map((documentCount: any, i: any) => {
                  const { label, count, type } = documentCount;
                  return (
                    <ListItem
                      key={`item-${i}`}
                      startIcon={imageForDocumentType(type)}
                      endIcon={ArrowRight}
                      title={label}
                      description={count + ' items'}
                      onClick={() => showGroup(type)}
                    />
                  );
                })}
              </>
            )}
          </LoadingIonList>
        </IonContent>
        <IonToolbar className="hk-mobile-digital-documents-groups-toolbar">
          <IonButton
            expand="block"
            color="primary"
            strong={true}
            onClick={() => dispatch(showDocumentsAddModal())}
          >
            Add Document
          </IonButton>
        </IonToolbar>
      </>
    );
  }

  return (
    <IonPage
      className={
        platformType === HKPlatform.DESKTOP && isDesktopWidth
          ? 'hk-desktop-digital-documents-group-page'
          : ''
      }
    >
      {platformType === HKPlatform.DESKTOP && isDesktopWidth
        ? DesktopDocumentsGroupView()
        : MobileDocumentsGroupView()}
      <IonModal
        className="hk-digital-documents-add-modal"
        isOpen={addModalVisible}
        backdropDismiss={false}
        onDidDismiss={() => {
          closeAddDocumentsModal();
        }}
      >
        <DocumentsAdd />
      </IonModal>
      <IonModal
        className="hk-desktop-digital-documents-edit-modal"
        isOpen={editDesktopModalVisible}
        onDidDismiss={() => dispatch(hideDesktopDocumentEditModal())}
      >
        <DocumentEdit />
      </IonModal>
    </IonPage>
  );
};

export default DocumentsGroups;

// Component for individual searched document items that can use hooks
const SearchedDocumentItem: React.FC<{
  document: SearchedDocument;
  onDocumentClick: (document: SearchedDocument) => void;
}> = ({ document, onDocumentClick }) => {
  const { name, file_url, thumbnail_url, type_label } = document;

  // Generate video thumbnail if it's a video file and no thumbnail_url exists
  const { thumbnail: videoThumbnail, loading: thumbnailLoading } =
    useVideoThumbnail(
      !thumbnail_url && isFileTypeVideo(file_url) ? file_url : '',
      1 // Capture frame at 1 second
    );

  return (
    <DocumentCard
      title={name}
      image={getDocumentImageSource(
        file_url,
        thumbnail_url,
        videoThumbnail,
        thumbnailLoading,
        type_label
      )}
      onClick={() => onDocumentClick(document)}
    />
  );
};
